import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

const DEFAULT_CURRENCY_DECIMALS = 2;
const MAX_QUANTITY = 10;

/**
 * @param {number} cents
 * @param {string} template
 * @returns {string}
 */
function formatCentsWithTemplate(cents, template) {
  if (!template) return (cents / 100).toFixed(2);

  return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, placeholder) => {
    if (typeof placeholder !== 'string') return '';

    let thousandsSeparator = ',';
    let decimalSeparator = '.';
    let precision = DEFAULT_CURRENCY_DECIMALS;

    if (placeholder === 'amount_no_decimals') {
      precision = 0;
    } else if (placeholder === 'amount_with_comma_separator') {
      thousandsSeparator = '.';
      decimalSeparator = ',';
    } else if (placeholder === 'amount_no_decimals_with_comma_separator') {
      thousandsSeparator = '.';
      precision = 0;
    } else if (placeholder === 'amount_with_space_separator') {
      thousandsSeparator = ' ';
      decimalSeparator = ',';
    } else if (placeholder === 'amount_with_period_and_space_separator') {
      thousandsSeparator = ' ';
      decimalSeparator = '.';
    } else if (placeholder === 'amount_with_apostrophe_separator') {
      thousandsSeparator = "'";
      decimalSeparator = '.';
    }

    const roundedNumber = (cents / 100).toFixed(precision);
    let [whole, fraction = ''] = roundedNumber.split('.');
    whole = whole.replace(/\d(?=(\d\d\d)+(?!\d))/g, (digit) => digit + thousandsSeparator);

    return precision <= 0 ? whole : `${whole}${decimalSeparator}${fraction.padEnd(precision, '0')}`;
  });
}

/**
 * @typedef {Object} PackItemData
 * @property {string} id
 * @property {number} variantId
 * @property {number} price
 * @property {string} title
 * @property {number} quantity
 */

/**
 * @typedef {Object} PackBuilderRefs
 * @property {HTMLButtonElement} [ctaButton]
 * @property {HTMLElement} [totalCompare]
 * @property {HTMLElement} [totalFinal]
 * @property {HTMLElement} [liveRegion]
 */

/**
 * @extends {Component<PackBuilderRefs>}
 */
class PackBuilderComponent extends Component {
  /** @type {Set<string>} */
  #selectedIds = new Set();

  /** @type {PackItemData[]} */
  #items = [];

  /** @type {AbortController | null} */
  #fetchAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    this.#keepDiscountPercentTogether();
    this.#bootstrapState();
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('change', this.#handleChange);
    this.refs.ctaButton?.addEventListener('click', this.addToCart);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('change', this.#handleChange);
    this.refs.ctaButton?.removeEventListener('click', this.addToCart);
    this.#fetchAbortController?.abort();
    this.#fetchAbortController = null;
  }

  updatedCallback() {
    super.updatedCallback();
    this.#keepDiscountPercentTogether();
    this.#bootstrapState();
  }

  /**
   * Keep "10 %" / "10%" on one line when translated titles wrap.
   */
  #keepDiscountPercentTogether() {
    const heading = this.querySelector('.pack-builder__heading');
    if (!(heading instanceof HTMLElement)) return;
    if (heading.querySelector('.pack-builder__pct')) return;

    const text = heading.textContent || '';
    if (!/\d+\s*%/.test(text)) return;

    heading.innerHTML = text.replace(/(\d+)\s*%/g, '<span class="pack-builder__pct">$1%</span>');
  }

  #bootstrapState() {
    this.#items = this.#readItemsFromDom();
    this.#selectedIds = new Set();

    for (const item of this.#items) {
      const row = this.#getRowElement(item.id);
      if (row?.classList.contains('is-selected')) {
        this.#selectedIds.add(item.id);
      }
    }

    this.#syncQuantitySelects();
    this.#updateTotals();
    this.#updateCtaState();
  }

  /**
   * @returns {PackItemData[]}
   */
  #readItemsFromDom() {
    /** @type {PackItemData[]} */
    const items = [];

    for (const row of this.querySelectorAll('[data-pack-item]')) {
      if (!(row instanceof HTMLElement)) continue;

      const id = row.dataset.packItem;
      const variantId = Number(row.dataset.variantId);
      const price = Number(row.dataset.price);
      const title = row.dataset.title || '';
      const select = row.querySelector('[data-pack-qty-select]');
      const quantity =
        select instanceof HTMLSelectElement ? Number(select.value) || 1 : Number(row.dataset.quantity) || 1;

      if (!id || !variantId || Number.isNaN(price)) continue;

      items.push({ id, variantId, price, title, quantity });
    }

    return items;
  }

  /**
   * @param {string} itemId
   * @returns {HTMLElement | null}
   */
  #getRowElement(itemId) {
    const row = this.querySelector(`[data-pack-item="${CSS.escape(itemId)}"]`);
    return row instanceof HTMLElement ? row : null;
  }

  /**
   * @param {string} itemId
   * @returns {PackItemData | undefined}
   */
  #getItem(itemId) {
    return this.#items.find((item) => item.id === itemId);
  }

  /**
   * @param {HTMLElement | null} row
   */
  #toggleFromRow(row) {
    if (!(row instanceof HTMLElement)) return;

    const itemId = row.dataset.packItem;
    if (!itemId) return;

    const isSelected = this.#selectedIds.has(itemId);
    this.#setRowSelected(itemId, !isSelected);
    this.#updateTotals();
    this.#updateCtaState();
    this.#syncQuantitySelects();
  }

  /**
   * @param {Event} event
   */
  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest('[data-pack-qty-control]')) return;

    const toggleButton = target.closest('[data-pack-toggle]');
    if (toggleButton) {
      event.preventDefault();
      event.stopPropagation();
      this.#toggleFromRow(toggleButton.closest('[data-pack-item]'));
    }
  };

  /**
   * @param {Event} event
   */
  #handleChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLSelectElement)) return;
    if (!target.matches('[data-pack-qty-select]')) return;

    const row = target.closest('[data-pack-item]');
    if (!(row instanceof HTMLElement)) return;

    const itemId = row.dataset.packItem;
    if (!itemId) return;

    const item = this.#getItem(itemId);
    if (!item) return;

    const quantity = Math.max(1, Math.min(MAX_QUANTITY, Number(target.value) || 1));
    item.quantity = quantity;
    row.dataset.quantity = String(quantity);
    target.value = String(quantity);

    if (!this.#selectedIds.has(itemId)) {
      this.#setRowSelected(itemId, true);
    }

    this.#updateTotals();
    this.#updateCtaState();
    this.#syncQuantitySelects();
  };

  /**
   * @param {string} itemId
   * @param {boolean} selected
   */
  #setRowSelected(itemId, selected) {
    const row = this.#getRowElement(itemId);
    if (!row) return;

    const button = row.querySelector('[data-pack-toggle]');
    const imageWrap = row.querySelector('.pack-builder__image-wrap');
    const title = row.dataset.title || '';

    row.classList.toggle('is-selected', selected);

    if (imageWrap instanceof HTMLElement) {
      imageWrap.classList.toggle('pack-builder__image-wrap--dimmed', !selected);
    }

    if (button instanceof HTMLButtonElement) {
      button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      const actionLabel = selected ? this.dataset.removeLabel : this.dataset.addLabel;
      if (actionLabel && title) {
        button.setAttribute('aria-label', `${actionLabel} ${title}`);
      }
    }

    if (selected) {
      this.#selectedIds.add(itemId);
    } else {
      this.#selectedIds.delete(itemId);
    }
  }

  #syncQuantitySelects() {
    for (const item of this.#items) {
      const row = this.#getRowElement(item.id);
      if (!row) continue;

      const select = row.querySelector('[data-pack-qty-select]');
      if (!(select instanceof HTMLSelectElement)) continue;

      select.value = String(item.quantity);
      select.disabled = !this.#selectedIds.has(item.id);
    }
  }

  #updateTotals() {
    const moneyFormat = this.dataset.moneyFormat || '';
    const discountPercent = Number(this.dataset.discountPercent) || 0;
    const minItems = Number(this.dataset.minItems) || 2;

    let subtotalCents = 0;
    for (const item of this.#items) {
      if (this.#selectedIds.has(item.id)) {
        subtotalCents += item.price * item.quantity;
      }
    }

    const selectedCount = this.#selectedIds.size;
    const discountApplies = selectedCount >= minItems && discountPercent > 0 && subtotalCents > 0;
    const finalCents = discountApplies
      ? Math.round(subtotalCents * (1 - discountPercent / 100))
      : subtotalCents;

    const totalCompare = this.refs.totalCompare;
    const totalFinal = this.refs.totalFinal;

    if (totalCompare instanceof HTMLElement) {
      if (discountApplies && subtotalCents !== finalCents) {
        totalCompare.hidden = false;
        totalCompare.textContent = formatCentsWithTemplate(subtotalCents, moneyFormat);
      } else {
        totalCompare.hidden = true;
        totalCompare.textContent = '';
      }
    }

    if (totalFinal instanceof HTMLElement) {
      if (selectedCount > 0) {
        totalFinal.hidden = false;
        const formattedFinal = formatCentsWithTemplate(finalCents, moneyFormat);
        totalFinal.textContent = discountApplies ? ` - ${formattedFinal}` : formattedFinal;
      } else {
        totalFinal.hidden = false;
        totalFinal.textContent = formatCentsWithTemplate(0, moneyFormat);
      }
    }
  }

  #updateCtaState() {
    const ctaButton = this.refs.ctaButton;
    if (!(ctaButton instanceof HTMLButtonElement)) return;

    const hasSelection = this.#selectedIds.size > 0;
    ctaButton.disabled = !hasSelection;
    ctaButton.setAttribute('aria-disabled', hasSelection ? 'false' : 'true');
  }

  /**
   * @param {Event} event
   */
  addToCart = async (event) => {
    event.preventDefault();

    const selectedItems = this.#items.filter((item) => this.#selectedIds.has(item.id));
    if (selectedItems.length === 0) return;

    this.#fetchAbortController?.abort();
    this.#fetchAbortController = new AbortController();

    const ctaButton = this.refs.ctaButton;
    if (!(ctaButton instanceof HTMLButtonElement)) return;

    const defaultLabel = ctaButton.textContent;
    ctaButton.disabled = true;
    ctaButton.setAttribute('aria-busy', 'true');

    try {
      const bundleId = this.dataset.bundleId || 'pack';
      const discountPercent = Number(this.dataset.discountPercent) || 0;
      const minItems = Number(this.dataset.minItems) || 2;
      const discountCode = (this.dataset.discountCode || '').trim();
      const discountApplies = selectedItems.length >= minItems && discountPercent > 0;

      /** @type {Record<string, string>} */
      const properties = {
        _pack_bundle: bundleId,
      };

      if (discountApplies) {
        properties._pack_discount_percent = String(discountPercent);
      }

      const sectionIds = this.#getCartSectionIds();

      const addBody = {
        items: selectedItems.map((item) => ({
          id: item.variantId,
          quantity: item.quantity,
          properties,
        })),
      };

      if (sectionIds.length > 0) {
        addBody.sections = sectionIds.join(',');
        addBody.sections_url = window.location.pathname;
      }

      const addResponse = await fetch(Theme.routes.cart_add_url, {
        ...fetchConfig('json', { body: JSON.stringify(addBody) }),
        signal: this.#fetchAbortController.signal,
      });

      const addData = await addResponse.json();

      if (addData.status) {
        this.#announce(addData.message || addData.description || 'Error');
        return;
      }

      if (discountApplies && discountCode) {
        await this.#applyDiscountCode(discountCode);
      }

      const addedText = this.dataset.addedLabel || Theme.translations.added;
      this.#announce(addedText);

      document.dispatchEvent(
        new CartAddEvent(addData, this.id, {
          source: 'pack-builder-component',
          itemCount: selectedItems.reduce((sum, item) => sum + item.quantity, 0),
          sections: addData.sections,
        })
      );
    } catch (error) {
      if (error instanceof Error && error.name !== 'AbortError') {
        throw error;
      }
    } finally {
      ctaButton.removeAttribute('aria-busy');
      this.#updateCtaState();
      if (defaultLabel) {
        ctaButton.textContent = defaultLabel;
      }
    }
  };

  /**
   * @param {string} discountCode
   */
  async #applyDiscountCode(discountCode) {
    const cartResponse = await fetch('/cart.js');
    const cart = await cartResponse.json();

    /** @type {string[]} */
    const existingCodes = (cart.discount_codes || [])
      .map((/** @type {{ code?: string }} */ entry) => entry.code)
      .filter((/** @type {string | undefined} */ code) => typeof code === 'string' && code.length > 0);

    if (existingCodes.includes(discountCode)) return;

    const mergedCodes = [...existingCodes, discountCode].join(',');

    await fetch(`${Theme.routes.cart_update_url}.js`, {
      ...fetchConfig('json', {
        body: JSON.stringify({ discount: mergedCodes }),
      }),
    });
  }

  /**
   * @returns {string[]}
   */
  #getCartSectionIds() {
    /** @type {string[]} */
    const sectionIds = [];

    document.querySelectorAll('cart-items-component').forEach((element) => {
      if (element instanceof HTMLElement && element.dataset.sectionId) {
        sectionIds.push(element.dataset.sectionId);
      }
    });

    return sectionIds;
  }

  /**
   * @param {string} message
   */
  #announce(message) {
    const { liveRegion } = this.refs;
    if (!(liveRegion instanceof HTMLElement)) return;

    liveRegion.textContent = message;
  }
}

if (!customElements.get('pack-builder-component')) {
  customElements.define('pack-builder-component', PackBuilderComponent);
}
